/**
 * Lot 6 — Campagnes : tableau de bord, modèle annuel, calendrier (Admin / Manager).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  campaignApi,
  domainsApi,
  ApiRequestError,
  type CampaignBoardDto,
  type CampaignDto,
  type CampaignFeaturesTrackingDto,
  type CampaignPhaseStatus,
  type CampaignProcessTrackingDto,
  type CampaignStep,
  type CampaignTransversalTrackingDto,
  type DomainDto,
  type YearTemplateDto,
} from "../api/api";
import { PageHeader } from "../components/ui/PageHeader";
import { useAuth } from "../context/AuthContext";
import { addCalendarDays, isEndOnOrAfterStart, isPlausibleIsoDate, todayIso } from "../utils/dates";
import { naturalSortByDomain } from "../utils/natural-sort";

const STEP_ORDER: CampaignStep[] = [
  "BPML_READY",
  "ENRICH",
  "PREEVAL",
  "EXPERT_ENTRY",
  "SUBMIT_DEADLINE",
  "VALIDATION",
  "ANALYSIS",
];

const PHASE_COLORS: Record<CampaignPhaseStatus, { bg: string; color: string }> = {
  not_started: { bg: "#f3f4f6", color: "#6b7280" },
  in_progress: { bg: "#dbeafe", color: "#1e40af" },
  blocked: { bg: "#fee2e2", color: "#991b1b" },
  delayed: { bg: "#ffedd5", color: "#9a3412" },
  done: { bg: "#dcfce7", color: "#166534" },
  skipped: { bg: "#f3e8ff", color: "#6b21a8" },
};

const PHASE_STATUS_ORDER: CampaignPhaseStatus[] = [
  "not_started",
  "in_progress",
  "blocked",
  "delayed",
  "done",
  "skipped",
];

const STATUS_FLOW = ["draft", "prep", "open", "closed"] as const;

type TemplateDraft = Record<string, { startsOn: string; endsOn: string; required: boolean }>;

function chainNextStart(draft: TemplateDraft, step: CampaignStep, endsOn: string): TemplateDraft {
  const idx = STEP_ORDER.indexOf(step);
  const nextStep = idx >= 0 ? STEP_ORDER[idx + 1] : undefined;
  if (!nextStep || !isPlausibleIsoDate(endsOn)) return draft;
  if ((draft[nextStep]?.startsOn || "").trim()) return draft;
  const chained = addCalendarDays(endsOn, 1);
  if (!chained) return draft;
  return {
    ...draft,
    [nextStep]: {
      startsOn: chained,
      endsOn: draft[nextStep]?.endsOn ?? "",
      required: draft[nextStep]?.required ?? nextStep !== "PREEVAL",
    },
  };
}

type StepDatePair = { startsOn: string; endsOn: string };

/** Ancre du calendrier natif : fin d’étape précédente, sinon début courant, sinon aujourd’hui. */
function dateEntryAnchor(
  step: CampaignStep,
  field: "startsOn" | "endsOn",
  draft: Partial<Record<string, StepDatePair>>
): string {
  const idx = STEP_ORDER.indexOf(step);
  const prev = idx > 0 ? STEP_ORDER[idx - 1] : undefined;
  const prevEnd = (prev && draft[prev]?.endsOn) || "";
  const currentStart = draft[step]?.startsOn || "";
  if (field === "startsOn") {
    if (isPlausibleIsoDate(prevEnd)) return prevEnd;
    return todayIso();
  }
  if (isPlausibleIsoDate(currentStart)) return currentStart;
  if (isPlausibleIsoDate(prevEnd)) return prevEnd;
  return todayIso();
}

/** Positionne une date vide dans le state React (pas de mutation DOM). */
function suggestDateIfEmpty(
  stored: string,
  step: CampaignStep,
  field: "startsOn" | "endsOn",
  draft: Partial<Record<string, StepDatePair>>,
  apply: (value: string) => void
) {
  if (stored) return;
  const anchor = dateEntryAnchor(step, field, draft);
  if (isPlausibleIsoDate(anchor)) apply(anchor);
}

type TabId = "board" | "suivi" | "template" | "calendrier";

type PhaseEdit = {
  campaignId: string;
  step: CampaignStep;
  phaseStatus: CampaignPhaseStatus;
  phaseComment: string;
};

function StatusPipeline({ status }: { status: string }) {
  const { t } = useTranslation();
  const idx = STATUS_FLOW.indexOf(status as (typeof STATUS_FLOW)[number]);
  return (
    <div style={{ display: "flex", gap: 4, flexWrap: "wrap", alignItems: "center" }}>
      {STATUS_FLOW.map((s, i) => {
        const active = i <= idx;
        const current = s === status;
        return (
          <span
            key={s}
            className={current ? "sm-badge" : undefined}
            style={{
              fontSize: 11,
              padding: current ? undefined : "2px 8px",
              borderRadius: 4,
              background: active && !current ? "var(--sm-blue-100)" : current ? undefined : "#f3f4f6",
              color: active ? "var(--sm-primary-dark)" : "var(--sm-text3)",
              fontWeight: current ? 700 : 500,
            }}
          >
            {t(`campaign_status_${s}`)}
            {i < STATUS_FLOW.length - 1 ? " →" : ""}
          </span>
        );
      })}
    </div>
  );
}

function milestoneFor(campaign: CampaignDto, step: CampaignStep) {
  return campaign.milestones.find((m) => m.step === step);
}

function SuiviDomainTable({
  tracking,
  t,
}: {
  tracking: CampaignProcessTrackingDto;
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  return (
    <>
      <p className="sm-muted" style={{ marginTop: 0 }}>
        {t("campaign_suivi_col_in_progress")}: {tracking.totals.inProgress} —{" "}
        {t("campaign_suivi_col_validated")}: {tracking.totals.validated} (
        {tracking.totals.total} {t("campaign_suivi_col_total").toLowerCase()})
      </p>
      <div className="sm-table-wrap">
        <table className="sm-table">
          <thead>
            <tr>
              <th>{t("domain")}</th>
              <th>{t("campaign_suivi_col_campaign_status")}</th>
              <th style={{ textAlign: "center" }}>{t("campaign_suivi_col_in_progress")}</th>
              <th style={{ textAlign: "center" }}>{t("campaign_suivi_col_validated")}</th>
              <th style={{ textAlign: "center" }}>{t("campaign_suivi_col_total")}</th>
            </tr>
          </thead>
          <tbody>
            {tracking.domains.map((d) => (
              <tr key={d.domain}>
                <td>{d.domain}</td>
                <td>{d.campaignStatus ? t(`campaign_status_${d.campaignStatus}`) : "—"}</td>
                <td style={{ textAlign: "center" }}>{d.inProgress}</td>
                <td style={{ textAlign: "center" }}>{d.validated}</td>
                <td style={{ textAlign: "center" }}>{d.total}</td>
              </tr>
            ))}
            {tracking.domains.length === 0 ? (
              <tr>
                <td colSpan={5} className="sm-muted" style={{ textAlign: "center" }}>
                  {t("no_data")}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </>
  );
}

/**
 * Lot 3 — Matrice de suivi « features applicatives » : domaine (de suivi, cf. backend) ×
 * application. Chaque case = complétude de la grille de features pour les experts du
 * domaine sur cette application ; un badge signale les applications sans aucune feature
 * active définie (anomalie de paramétrage, cf. `hasMissingFeatures`).
 */
function SuiviFeaturesMatrix({
  tracking,
  t,
}: {
  tracking: CampaignFeaturesTrackingDto;
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  return (
    <div className="sm-table-wrap">
      <table className="sm-table">
        <thead>
          <tr>
            <th>{t("domain")}</th>
            <th style={{ textAlign: "center" }}>{t("campaign_suivi_col_experts")}</th>
            {tracking.applications.map((app) => (
              <th key={app.id} style={{ textAlign: "center" }}>
                {app.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {tracking.domains.map((d) => (
            <tr key={d.domain}>
              <td>{d.domain}</td>
              <td style={{ textAlign: "center" }}>{d.totalExperts}</td>
              {d.cells.map((cell) => (
                <td key={cell.applicationId} style={{ textAlign: "center" }}>
                  {cell.completed}/{cell.total}
                  {cell.hasMissingFeatures ? (
                    <div>
                      <span
                        className="sm-badge"
                        title={t("campaign_suivi_features_missing_hint")}
                        style={{ fontSize: 10, background: "#fef3c7", color: "#92400e" }}
                      >
                        {t("campaign_suivi_features_missing_badge")}
                      </span>
                    </div>
                  ) : null}
                </td>
              ))}
            </tr>
          ))}
          {tracking.domains.length === 0 ? (
            <tr>
              <td
                colSpan={2 + tracking.applications.length}
                className="sm-muted"
                style={{ textAlign: "center" }}
              >
                {t("no_data")}
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}

export function CampaignsPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const isAdmin = (user?.accessLevel ?? 0) >= 100;
  const yearNow = new Date().getFullYear();

  // Deep-link depuis le Dashboard (ex. /campaigns?tab=suivi&year=2026) — lu une seule
  // fois à l'initialisation, le reste de la page garde son fonctionnement habituel.
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab");
  const initialYear = Number(searchParams.get("year"));

  const [year, setYear] = useState(
    Number.isFinite(initialYear) && initialYear > 0 ? initialYear : yearNow
  );
  const [tab, setTab] = useState<TabId>(
    initialTab === "board" || initialTab === "suivi" || initialTab === "template" || initialTab === "calendrier"
      ? initialTab
      : "board"
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Board
  const [board, setBoard] = useState<CampaignBoardDto | null>(null);
  const [phaseEdit, setPhaseEdit] = useState<PhaseEdit | null>(null);

  // Suivi process (lot 1) + transversal (lot 2) + features applicatives (lot 3)
  const [processTracking, setProcessTracking] = useState<CampaignProcessTrackingDto | null>(null);
  const [transversalTracking, setTransversalTracking] =
    useState<CampaignTransversalTrackingDto | null>(null);
  const [featuresTracking, setFeaturesTracking] = useState<CampaignFeaturesTrackingDto | null>(
    null
  );

  // Template
  const [template, setTemplate] = useState<YearTemplateDto | null>(null);
  const [templateDraft, setTemplateDraft] = useState<TemplateDraft>({});
  const [generateResult, setGenerateResult] = useState<{
    created: number;
    updated: number;
    skipped: number;
  } | null>(null);

  // Calendrier
  const [domains, setDomains] = useState<DomainDto[]>([]);
  const [list, setList] = useState<CampaignDto[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<CampaignDto | null>(null);
  const [dateDraft, setDateDraft] = useState<
    Record<string, { startsOn: string; endsOn: string }>
  >({});
  /** Dates only suggested on focus — do not persist on blur unless the user changed them. */
  const primedDates = useRef(new Set<string>());

  const canEditDates = isAdmin || Boolean(detail?.managersCanEditMilestones);
  const canGenerate = Boolean(template?.updatedAt);
  const boardCampaigns = board ? naturalSortByDomain(board.campaigns) : [];
  const calendarCampaigns = naturalSortByDomain(list);

  const existingCampaigns = tab === "calendrier" ? list : (board?.campaigns ?? list);
  const existingDomainCodes = new Set(existingCampaigns.map((c) => c.domain.toUpperCase()));
  const missingDomains = domains.filter(
    (d) => d.active && !existingDomainCodes.has(d.code.toUpperCase())
  );

  function syncDateDraft(campaign: CampaignDto) {
    const next: Record<string, { startsOn: string; endsOn: string }> = {};
    for (const m of campaign.milestones) {
      next[m.step] = { startsOn: m.startsOn ?? "", endsOn: m.endsOn ?? "" };
    }
    setDateDraft(next);
  }

  function syncTemplateDraft(tpl: YearTemplateDto) {
    const next: Record<string, { startsOn: string; endsOn: string; required: boolean }> = {};
    for (const step of STEP_ORDER) {
      const m = tpl.milestones.find((x) => x.step === step);
      next[step] = {
        startsOn: m?.startsOn ?? "",
        endsOn: m?.endsOn ?? "",
        required: m?.required ?? step !== "PREEVAL",
      };
    }
    setTemplateDraft(next);
  }

  const loadBoard = useCallback(async () => {
    const res = await campaignApi.board(year);
    setBoard(res.board);
  }, [year]);

  const loadProcessTracking = useCallback(async () => {
    const res = await campaignApi.processTracking(year);
    setProcessTracking(res.tracking);
  }, [year]);

  const loadTransversalTracking = useCallback(async () => {
    const res = await campaignApi.transversalTracking(year);
    setTransversalTracking(res.tracking);
  }, [year]);

  const loadFeaturesTracking = useCallback(async () => {
    const res = await campaignApi.featuresTracking(year);
    setFeaturesTracking(res.tracking);
  }, [year]);

  const loadTemplate = useCallback(async () => {
    const res = await campaignApi.getTemplate(year);
    setTemplate(res.template);
    syncTemplateDraft(res.template);
  }, [year]);

  const loadCalendar = useCallback(
    async (preferId?: string | null) => {
      const res = await campaignApi.list({ year });
      setList(res.campaigns);
      const next =
        preferId ??
        (selectedId && res.campaigns.some((c) => c.id === selectedId) ? selectedId : null) ??
        res.campaigns[0]?.id ??
        null;
      setSelectedId(next);
      if (next) {
        const d = await campaignApi.get(next);
        setDetail(d.campaign);
        syncDateDraft(d.campaign);
      } else {
        setDetail(null);
        setDateDraft({});
      }
    },
    [year, selectedId]
  );

  useEffect(() => {
    setGenerateResult(null);
  }, [year]);

  useEffect(() => {
    void (async () => {
      setError(null);
      setPhaseEdit(null);
      try {
        const dom = await domainsApi.list();
        setDomains(dom.domains);
        await loadBoard();
        await loadTemplate();
        if (tab === "calendrier") {
          await loadCalendar();
        }
        if (tab === "suivi") {
          await loadProcessTracking();
          await loadTransversalTracking();
          await loadFeaturesTracking();
        }
      } catch (err) {
        setError(err instanceof ApiRequestError ? err.message : t("analytics_load_failed"));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, tab]);

  async function selectCampaign(id: string) {
    setSelectedId(id);
    setError(null);
    try {
      const r = await campaignApi.get(id);
      setDetail(r.campaign);
      syncDateDraft(r.campaign);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("load_failed"));
    }
  }

  function openCalendarFor(campaignId: string) {
    setSelectedId(campaignId);
    setTab("calendrier");
  }

  async function savePhaseEdit() {
    if (!phaseEdit) return;
    setBusy(true);
    setError(null);
    try {
      await campaignApi.patchPhase(phaseEdit.campaignId, {
        step: phaseEdit.step,
        phaseStatus: phaseEdit.phaseStatus,
        phaseComment: phaseEdit.phaseComment || null,
      });
      setPhaseEdit(null);
      await loadBoard();
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("save_failed"));
    } finally {
      setBusy(false);
    }
  }

  function firstTemplateRangeError(draft: TemplateDraft): CampaignStep | null {
    for (const step of STEP_ORDER) {
      const d = draft[step];
      if (!d?.startsOn || !d?.endsOn) continue;
      if (!isPlausibleIsoDate(d.startsOn) || !isPlausibleIsoDate(d.endsOn)) continue;
      if (!isEndOnOrAfterStart(d.startsOn, d.endsOn)) return step;
    }
    return null;
  }

  async function saveTemplateWith(draft: TemplateDraft) {
    if (!isAdmin) return;
    const invalid = STEP_ORDER.some((step) => {
      const d = draft[step];
      return (
        (Boolean(d?.startsOn) && !isPlausibleIsoDate(d.startsOn)) ||
        (Boolean(d?.endsOn) && !isPlausibleIsoDate(d.endsOn))
      );
    });
    if (invalid) return;
    const rangeStep = firstTemplateRangeError(draft);
    if (rangeStep) {
      setError(
        t("campaign_step_end_before_start", { step: t(`campaign_step_${rangeStep}`) })
      );
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const milestones = STEP_ORDER.map((step) => ({
        step,
        startsOn: draft[step]?.startsOn || null,
        endsOn: draft[step]?.endsOn || null,
        required: draft[step]?.required ?? step !== "PREEVAL",
      }));
      const res = await campaignApi.saveTemplate({
        year,
        notes: template?.notes ?? null,
        milestones,
      });
      setTemplate(res.template);
      syncTemplateDraft(res.template);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("save_template_failed"));
    } finally {
      setBusy(false);
    }
  }

  async function saveTemplateNow() {
    await saveTemplateWith(templateDraft);
  }

  function setTemplateField(
    step: CampaignStep,
    field: "startsOn" | "endsOn",
    value: string
  ) {
    setTemplateDraft((prev) => ({
      ...prev,
      [step]: {
        startsOn: prev[step]?.startsOn ?? "",
        endsOn: prev[step]?.endsOn ?? "",
        required: prev[step]?.required ?? step !== "PREEVAL",
        [field]: value,
      },
    }));
  }

  function onTemplateDateBlur(step: CampaignStep, field: "startsOn" | "endsOn", value: string) {
    let next: TemplateDraft = {
      ...templateDraft,
      [step]: {
        startsOn: templateDraft[step]?.startsOn ?? "",
        endsOn: templateDraft[step]?.endsOn ?? "",
        required: templateDraft[step]?.required ?? step !== "PREEVAL",
        [field]: value,
      },
    };
    if (field === "endsOn") next = chainNextStart(next, step, value);
    setTemplateDraft(next);
    void saveTemplateWith(next);
  }

  async function runGenerate() {
    if (!isAdmin) return;
    if (!template?.updatedAt) {
      setError(t("campaign_generate_need_template", { year }));
      return;
    }
    const existing = board?.campaigns ?? list;
    const unused = existing.filter((c) => c.status === "draft" || c.status === "prep");
    if (unused.length > 0) {
      const ok = window.confirm(
        t("campaign_create_all_confirm_update", {
          count: unused.length,
          domains: unused.map((c) => c.domain).join(", "),
        })
      );
      if (!ok) return;
    }
    setBusy(true);
    setError(null);
    setGenerateResult(null);
    try {
      const res = await campaignApi.generate({ year });
      setGenerateResult({
        created: res.created,
        updated: res.updated,
        skipped: res.skipped,
      });
      await loadBoard();
      if (tab === "calendrier") {
        await loadCalendar();
      }
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("generate_failed"));
    } finally {
      setBusy(false);
    }
  }

  async function commitMilestoneDate(
    step: CampaignStep,
    field: "startsOn" | "endsOn",
    value: string
  ) {
    if (!detail || !canEditDates) return;
    const serverVal = detail.milestones.find((m) => m.step === step)?.[field] ?? "";
    const nextVal = value || null;
    if ((serverVal ?? "") === (nextVal ?? "")) return;
    if (nextVal && !isPlausibleIsoDate(nextVal)) return;

    setError(null);
    try {
      const milestones: Array<{
        step: CampaignStep;
        startsOn?: string | null;
        endsOn?: string | null;
      }> = [{ step, [field]: nextVal }];

      if (field === "endsOn" && nextVal && isPlausibleIsoDate(nextVal)) {
        const idx = STEP_ORDER.indexOf(step);
        const nextStep = idx >= 0 ? STEP_ORDER[idx + 1] : undefined;
        if (nextStep) {
          const nextStart =
            dateDraft[nextStep]?.startsOn ||
            detail.milestones.find((m) => m.step === nextStep)?.startsOn ||
            "";
          const chained = !nextStart ? addCalendarDays(nextVal, 1) : null;
          if (chained) {
            milestones.push({ step: nextStep, startsOn: chained });
          }
        }
      }

      const res = await campaignApi.patch(detail.id, { milestones });
      setDetail(res.campaign);
      syncDateDraft(res.campaign);
      setList((prev) => prev.map((c) => (c.id === res.campaign.id ? res.campaign : c)));
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("update_failed"));
      syncDateDraft(detail);
    }
  }

  function setDraftField(step: CampaignStep, field: "startsOn" | "endsOn", value: string) {
    setDateDraft((prev) => ({
      ...prev,
      [step]: {
        startsOn: prev[step]?.startsOn ?? "",
        endsOn: prev[step]?.endsOn ?? "",
        [field]: value,
      },
    }));
  }

  async function toggleManagerDates(checked: boolean) {
    if (!detail || !isAdmin) return;
    setError(null);
    try {
      const res = await campaignApi.patch(detail.id, {
        managersCanEditMilestones: checked,
      });
      setDetail(res.campaign);
      setList((prev) => prev.map((c) => (c.id === res.campaign.id ? res.campaign : c)));
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("update_failed"));
    }
  }

  async function runTransition(kind: "prep" | "open" | "close") {
    if (!detail) return;
    setBusy(true);
    setError(null);
    try {
      const res =
        kind === "prep"
          ? await campaignApi.prep(detail.id)
          : kind === "open"
            ? await campaignApi.open(detail.id)
            : await campaignApi.close(detail.id);
      setDetail(res.campaign);
      await loadCalendar(res.campaign.id);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("transition_failed"));
    } finally {
      setBusy(false);
    }
  }

  const boardSteps = board?.steps?.length ? board.steps : STEP_ORDER;

  return (
    <div className="sm-page">
      <PageHeader title={t("campaigns")} subtitle={t("campaigns_sub")} />
      {error ? <p className="sm-error">{error}</p> : null}

      <div className="sm-toolbar" style={{ gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
        <label className="sm-label" style={{ margin: 0 }}>
          {t("year")}
          <input
            className="sm-input"
            type="number"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            style={{ width: 100, marginLeft: 8 }}
          />
        </label>
        <div className="sm-form-actions" style={{ margin: 0 }}>
          <button
            type="button"
            className={`sm-btn${tab === "board" ? " sm-btn-primary" : ""}`}
            onClick={() => setTab("board")}
          >
            {t("campaign_tab_board")}
          </button>
          <button
            type="button"
            className={`sm-btn${tab === "suivi" ? " sm-btn-primary" : ""}`}
            onClick={() => setTab("suivi")}
          >
            {t("campaign_tab_suivi")}
          </button>
          <button
            type="button"
            className={`sm-btn${tab === "template" ? " sm-btn-primary" : ""}`}
            onClick={() => setTab("template")}
          >
            {t("campaign_tab_template")}
          </button>
          <button
            type="button"
            className={`sm-btn${tab === "calendrier" ? " sm-btn-primary" : ""}`}
            onClick={() => setTab("calendrier")}
          >
            {t("campaign_tab_calendar")}
          </button>
        </div>
        {isAdmin ? (
          <button
            type="button"
            className="sm-btn sm-btn-primary"
            disabled={busy || !canGenerate}
            title={
              canGenerate ? t("campaign_create_all_hint") : t("campaign_generate_need_template", { year })
            }
            onClick={() => void runGenerate()}
          >
            {t("campaign_create_all")}
          </button>
        ) : null}
      </div>
      {isAdmin && missingDomains.length > 0 ? (
        <p className="sm-muted" style={{ marginTop: 0 }}>
          {t("campaign_missing_domains", { count: missingDomains.length, year })}
        </p>
      ) : null}
      {generateResult ? (
        <p style={{ marginTop: 0 }}>{t("campaign_generate_done", generateResult)}</p>
      ) : null}

      {tab === "board" ? (
        <div className="sm-card sm-card-pad">
          <h2 style={{ marginTop: 0 }}>{t("campaign_board_phases")}</h2>
          <p className="sm-muted" style={{ marginTop: 0 }}>
            {t("campaign_board_hint")}
          </p>

          {board ? (
            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                marginBottom: 16,
              }}
            >
              {(
                [
                  ["blocked", t("campaign_kpi_blocked"), board.summary.blocked],
                  ["delayed", t("campaign_kpi_delayed"), board.summary.delayed],
                  ["inProgress", t("campaign_kpi_in_progress"), board.summary.inProgress],
                  ["overdue", t("campaign_kpi_overdue"), board.summary.overdue],
                ] as const
              ).map(([key, label, count]) => (
                <span
                  key={key}
                  className="sm-badge"
                  style={{
                    fontSize: 13,
                    padding: "6px 12px",
                    background:
                      key === "blocked"
                        ? PHASE_COLORS.blocked.bg
                        : key === "delayed" || key === "overdue"
                          ? PHASE_COLORS.delayed.bg
                          : PHASE_COLORS.in_progress.bg,
                    color:
                      key === "blocked"
                        ? PHASE_COLORS.blocked.color
                        : key === "delayed" || key === "overdue"
                          ? PHASE_COLORS.delayed.color
                          : PHASE_COLORS.in_progress.color,
                  }}
                >
                  {label}&nbsp;: <strong>{count}</strong>
                </span>
              ))}
            </div>
          ) : null}

          <div className="sm-table-wrap">
            <table className="sm-table">
              <thead>
                <tr>
                  <th>{t("domain")}</th>
                  <th>{t("status")}</th>
                  <th>{t("deadline")}</th>
                  {boardSteps.map((step) => (
                    <th key={step} style={{ fontSize: 11, whiteSpace: "nowrap" }}>
                      {t(`campaign_step_${step}`)}
                    </th>
                  ))}
                  <th />
                </tr>
              </thead>
              <tbody>
                {!board || boardCampaigns.length === 0 ? (
                  <tr>
                    <td
                      colSpan={4 + boardSteps.length}
                      className="sm-muted"
                      style={{ textAlign: "center" }}
                    >
                      {t("campaign_none_year")}
                      {isAdmin ? (
                        <div style={{ marginTop: 12 }}>
                          <button
                            type="button"
                            className="sm-btn sm-btn-primary"
                            disabled={busy || !canGenerate}
                            title={
                              canGenerate
                                ? t("campaign_create_all_hint")
                                : t("campaign_generate_need_template", { year })
                            }
                            onClick={() => void runGenerate()}
                          >
                            {t("campaign_create_all")}
                          </button>
                          <p className="sm-muted" style={{ margin: "8px 0 0" }}>
                            {canGenerate
                              ? t("campaign_create_all_hint")
                              : t("campaign_generate_need_template", { year })}
                          </p>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ) : (
                  boardCampaigns.map((c) => (
                    <tr key={c.id}>
                      <td>
                        <strong>{c.domain}</strong>
                      </td>
                      <td>
                        <span className="sm-badge">{t(`campaign_status_${c.status}`)}</span>
                      </td>
                      <td>{c.submitDeadline ?? "—"}</td>
                      {boardSteps.map((step) => {
                        const m = milestoneFor(c, step);
                        const status = m?.phaseStatus ?? "not_started";
                        const colors = PHASE_COLORS[status];
                        const editing =
                          phaseEdit?.campaignId === c.id && phaseEdit.step === step;
                        return (
                          <td
                            key={step}
                            style={{ verticalAlign: "top", minWidth: editing ? 200 : 90 }}
                          >
                            {editing ? (
                              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                <select
                                  className="sm-input"
                                  value={phaseEdit.phaseStatus}
                                  onChange={(e) =>
                                    setPhaseEdit({
                                      ...phaseEdit,
                                      phaseStatus: e.target.value as CampaignPhaseStatus,
                                    })
                                  }
                                >
                                  {PHASE_STATUS_ORDER.map(
                                    (ps) => (
                                      <option key={ps} value={ps}>
                                        {t(`campaign_phase_${ps}`)}
                                      </option>
                                    )
                                  )}
                                </select>
                                <textarea
                                  className="sm-input"
                                  rows={2}
                                  placeholder={t("comment_ph")}
                                  value={phaseEdit.phaseComment}
                                  onChange={(e) =>
                                    setPhaseEdit({
                                      ...phaseEdit,
                                      phaseComment: e.target.value,
                                    })
                                  }
                                />
                                <div className="sm-form-actions" style={{ margin: 0 }}>
                                  <button
                                    type="button"
                                    className="sm-btn sm-btn-primary"
                                    disabled={busy}
                                    onClick={() => void savePhaseEdit()}
                                  >
                                    {t("save")}
                                  </button>
                                  <button
                                    type="button"
                                    className="sm-btn"
                                    disabled={busy}
                                    onClick={() => setPhaseEdit(null)}
                                  >
                                    {t("cancel")}
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <button
                                type="button"
                                className="sm-btn"
                                title={m?.phaseComment ?? undefined}
                                style={{
                                  width: "100%",
                                  fontSize: 11,
                                  padding: "4px 6px",
                                  background: colors.bg,
                                  color: colors.color,
                                  border: m?.overdue ? "2px solid #c2410c" : undefined,
                                  fontWeight: 600,
                                }}
                                onClick={() =>
                                  setPhaseEdit({
                                    campaignId: c.id,
                                    step,
                                    phaseStatus: status,
                                    phaseComment: m?.phaseComment ?? "",
                                  })
                                }
                              >
                                {t(`campaign_phase_${status}`)}
                                {m?.overdue ? " ⚠" : ""}
                              </button>
                            )}
                          </td>
                        );
                      })}
                      <td>
                        <button
                          type="button"
                          className="sm-btn"
                          onClick={() => openCalendarFor(c.id)}
                        >
                          {t("calendar")}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {tab === "suivi" ? (
        <div className="sm-card sm-card-pad">
          <h2 style={{ marginTop: 0 }}>{t("campaign_suivi_title")}</h2>
          <p className="sm-muted" style={{ marginTop: 0 }}>
            {t("campaign_suivi_hint")}
          </p>

          <h3 style={{ marginTop: 24 }}>{t("campaign_suivi_process_section")}</h3>
          {processTracking ? (
            <SuiviDomainTable tracking={processTracking} t={t} />
          ) : (
            <p className="sm-muted">{t("loading")}</p>
          )}

          <h3 style={{ marginTop: 32 }}>{t("campaign_suivi_transversal_section")}</h3>
          {transversalTracking ? (
            <SuiviDomainTable tracking={transversalTracking} t={t} />
          ) : (
            <p className="sm-muted">{t("loading")}</p>
          )}

          <h3 style={{ marginTop: 32 }}>{t("campaign_suivi_features_section")}</h3>
          <p className="sm-muted" style={{ marginTop: 0 }}>
            {t("campaign_suivi_features_hint")}
          </p>
          {featuresTracking ? (
            <SuiviFeaturesMatrix tracking={featuresTracking} t={t} />
          ) : (
            <p className="sm-muted">{t("loading")}</p>
          )}
        </div>
      ) : null}

      {tab === "template" ? (
        <div className="sm-card sm-card-pad">
          <h2 style={{ marginTop: 0 }}>{t("year_template_title", { year })}</h2>
          {!isAdmin ? (
            <p className="sm-muted">
              {t("campaign_template_manager_hint")}
            </p>
          ) : (
            <>
              <p className="sm-muted" style={{ marginTop: 0 }}>
                {t("campaign_template_admin_hint")} {t("campaign_template_date_hint")}
              </p>
              <div className="sm-table-wrap">
                <table className="sm-table">
                  <thead>
                    <tr>
                      <th>{t("step")}</th>
                      <th>{t("starts_on")}</th>
                      <th>{t("ends_on")}</th>
                      <th>{t("required_short")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {STEP_ORDER.map((step) => {
                      const draft = templateDraft[step] ?? {
                        startsOn: "",
                        endsOn: "",
                        required: step !== "PREEVAL",
                      };
                      const rangeInvalid =
                        Boolean(draft.startsOn) &&
                        Boolean(draft.endsOn) &&
                        isPlausibleIsoDate(draft.startsOn) &&
                        isPlausibleIsoDate(draft.endsOn) &&
                        !isEndOnOrAfterStart(draft.startsOn, draft.endsOn);
                      return (
                        <tr key={step}>
                          <td>{t(`campaign_step_${step}`)}</td>
                          <td>
                            <input
                              className="sm-input"
                              type="date"
                              value={draft.startsOn}
                              max={draft.endsOn || undefined}
                              aria-invalid={rangeInvalid}
                              onFocus={() =>
                                suggestDateIfEmpty(
                                  draft.startsOn,
                                  step,
                                  "startsOn",
                                  templateDraft,
                                  (value) => {
                                    primedDates.current.add(`tpl:${step}:startsOn`);
                                    setTemplateField(step, "startsOn", value);
                                  }
                                )
                              }
                              onChange={(e) => {
                                primedDates.current.delete(`tpl:${step}:startsOn`);
                                setTemplateField(step, "startsOn", e.target.value);
                              }}
                              onBlur={(e) => {
                                if (primedDates.current.delete(`tpl:${step}:startsOn`)) {
                                  setTemplateField(step, "startsOn", "");
                                  return;
                                }
                                onTemplateDateBlur(step, "startsOn", e.target.value);
                              }}
                            />
                          </td>
                          <td>
                            <input
                              className="sm-input"
                              type="date"
                              value={draft.endsOn}
                              min={draft.startsOn || undefined}
                              aria-invalid={rangeInvalid}
                              onFocus={() =>
                                suggestDateIfEmpty(
                                  draft.endsOn,
                                  step,
                                  "endsOn",
                                  templateDraft,
                                  (value) => {
                                    primedDates.current.add(`tpl:${step}:endsOn`);
                                    setTemplateField(step, "endsOn", value);
                                  }
                                )
                              }
                              onChange={(e) => {
                                primedDates.current.delete(`tpl:${step}:endsOn`);
                                setTemplateField(step, "endsOn", e.target.value);
                              }}
                              onBlur={(e) => {
                                if (primedDates.current.delete(`tpl:${step}:endsOn`)) {
                                  setTemplateField(step, "endsOn", "");
                                  return;
                                }
                                onTemplateDateBlur(step, "endsOn", e.target.value);
                              }}
                            />
                          </td>
                          <td>{draft.required ? t("yes") : t("no")}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="sm-form-actions" style={{ marginTop: 12 }}>
                <button
                  type="button"
                  className="sm-btn"
                  disabled={busy}
                  onClick={() => void saveTemplateNow()}
                >
                  {t("save_template")}
                </button>
                <button
                  type="button"
                  className="sm-btn sm-btn-primary"
                  disabled={busy || !canGenerate}
                  title={
                    canGenerate
                      ? t("campaign_create_all_hint")
                      : t("campaign_generate_need_template", { year })
                  }
                  onClick={() => void runGenerate()}
                >
                  {t("campaign_create_all")}
                </button>
              </div>
              {template?.updatedAt ? (
                <p className="sm-muted" style={{ fontSize: 12, marginTop: 8 }}>
                  {t("template_last_update")}&nbsp;:{" "}
                  {new Date(template.updatedAt).toLocaleString(i18n.language)}
                </p>
              ) : null}
            </>
          )}
        </div>
      ) : null}

      {tab === "calendrier" ? (
        <div style={{ display: "grid", gridTemplateColumns: "220px 1fr", gap: 16 }}>
          <div className="sm-card sm-card-pad">
            <h3 style={{ marginTop: 0 }}>{t("domains")}</h3>
            {calendarCampaigns.length === 0 ? (
              <div>
                <p className="sm-muted">{t("campaign_none_year")}</p>
                {isAdmin ? (
                  <button
                    type="button"
                    className="sm-btn sm-btn-primary"
                    disabled={busy || !canGenerate}
                    title={
                      canGenerate
                        ? t("campaign_create_all_hint")
                        : t("campaign_generate_need_template", { year })
                    }
                    onClick={() => void runGenerate()}
                  >
                    {t("campaign_create_all")}
                  </button>
                ) : null}
              </div>
            ) : (
              <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {calendarCampaigns.map((c) => (
                  <li key={c.id}>
                    <button
                      type="button"
                      className="sm-btn"
                      style={{
                        width: "100%",
                        textAlign: "left",
                        marginBottom: 6,
                        background: c.id === selectedId ? "rgba(31,78,121,0.1)" : undefined,
                      }}
                      onClick={() => void selectCampaign(c.id)}
                    >
                      <strong>{c.domain}</strong>
                      <div className="sm-muted" style={{ fontSize: 12 }}>
                        {t(`campaign_status_${c.status}`)}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {isAdmin && missingDomains.length > 0 && calendarCampaigns.length > 0 ? (
              <button
                type="button"
                className="sm-btn sm-btn-primary"
                style={{ marginTop: 12, width: "100%" }}
                disabled={busy || !canGenerate}
                title={
                  canGenerate
                    ? t("campaign_create_all_hint")
                    : t("campaign_generate_need_template", { year })
                }
                onClick={() => void runGenerate()}
              >
                {t("campaign_create_all")}
              </button>
            ) : null}
          </div>

          <div className="sm-card sm-card-pad">
            {!detail ? (
              <p className="sm-muted">{t("select_campaign")}</p>
            ) : (
              <>
                <div className="sm-toolbar" style={{ justifyContent: "space-between" }}>
                  <div>
                    <h2 style={{ margin: 0 }}>
                      {detail.domain} · {detail.year}
                    </h2>
                    <StatusPipeline status={detail.status} />
                  </div>
                  <div className="sm-form-actions">
                    {(detail.status === "draft" || detail.status === "prep") && (
                      <button
                        type="button"
                        className="sm-btn"
                        disabled={busy}
                        onClick={() => void runTransition("prep")}
                      >
                        {t("to_prep")}
                      </button>
                    )}
                    {(detail.status === "draft" || detail.status === "prep") && (
                      <button
                        type="button"
                        className="sm-btn sm-btn-primary"
                        disabled={busy}
                        onClick={() => void runTransition("open")}
                      >
                        {t("open")}
                      </button>
                    )}
                    {detail.status === "open" && (
                      <button
                        type="button"
                        className="sm-btn"
                        disabled={busy}
                        onClick={() => void runTransition("close")}
                      >
                        {t("close_campaign")}
                      </button>
                    )}
                  </div>
                </div>

                {isAdmin ? (
                  <label
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                      margin: "8px 0",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={detail.managersCanEditMilestones}
                      onChange={(e) => void toggleManagerDates(e.target.checked)}
                    />
                    {t("allow_manager_dates")}
                  </label>
                ) : !canEditDates ? (
                  <p className="sm-muted" style={{ fontSize: 13 }}>
                    {t("dates_readonly_admin")}
                  </p>
                ) : null}

                <p className="sm-muted" style={{ fontSize: 13 }}>
                  {t("campaign_open_hint")}
                </p>
                <div className="sm-table-wrap">
                  <table className="sm-table">
                    <thead>
                      <tr>
                        <th>{t("step")}</th>
                        <th>{t("starts_on")}</th>
                        <th>{t("ends_on")}</th>
                        <th>{t("required_short")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.milestones.map((m) => {
                        const draft = dateDraft[m.step] ?? {
                          startsOn: m.startsOn ?? "",
                          endsOn: m.endsOn ?? "",
                        };
                        const locked = detail.status === "closed" || !canEditDates;
                        return (
                          <tr key={m.step}>
                            <td>{t(`campaign_step_${m.step}`)}</td>
                            <td>
                              <input
                                className="sm-input"
                                type="date"
                                disabled={locked}
                                value={draft.startsOn}
                                max={draft.endsOn || undefined}
                                onFocus={() =>
                                  suggestDateIfEmpty(
                                    draft.startsOn,
                                    m.step,
                                    "startsOn",
                                    dateDraft,
                                    (value) => {
                                      primedDates.current.add(`cal:${m.step}:startsOn`);
                                      setDraftField(m.step, "startsOn", value);
                                    }
                                  )
                                }
                                onChange={(e) => {
                                  primedDates.current.delete(`cal:${m.step}:startsOn`);
                                  setDraftField(m.step, "startsOn", e.target.value);
                                }}
                                onBlur={(e) => {
                                  if (primedDates.current.delete(`cal:${m.step}:startsOn`)) {
                                    setDraftField(m.step, "startsOn", "");
                                    return;
                                  }
                                  void commitMilestoneDate(m.step, "startsOn", e.target.value);
                                }}
                              />
                            </td>
                            <td>
                              <input
                                className="sm-input"
                                type="date"
                                disabled={locked}
                                value={draft.endsOn}
                                min={draft.startsOn || undefined}
                                onFocus={() =>
                                  suggestDateIfEmpty(
                                    draft.endsOn,
                                    m.step,
                                    "endsOn",
                                    dateDraft,
                                    (value) => {
                                      primedDates.current.add(`cal:${m.step}:endsOn`);
                                      setDraftField(m.step, "endsOn", value);
                                    }
                                  )
                                }
                                onChange={(e) => {
                                  primedDates.current.delete(`cal:${m.step}:endsOn`);
                                  setDraftField(m.step, "endsOn", e.target.value);
                                }}
                                onBlur={(e) => {
                                  if (primedDates.current.delete(`cal:${m.step}:endsOn`)) {
                                    setDraftField(m.step, "endsOn", "");
                                    return;
                                  }
                                  void commitMilestoneDate(m.step, "endsOn", e.target.value);
                                }}
                              />
                            </td>
                            <td>{m.required ? t("yes") : t("no")}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {(detail.status === "prep" || detail.status === "open") && (
                  <p style={{ marginTop: 12 }}>
                    <Link to="/preevals" className="sm-link">
                      {t("preevals_manager_link")}
                    </Link>
                    <span className="sm-muted">
                      {" "}
                      {t("preevals_optional_hint")}
                    </span>
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
